import api from '../../../services/api.ts';
import type { CalendarEvent, GoogleCalendarConnection } from '../types/calendar.types.ts';

const EVENTS_CACHE_KEY = 'tbkmeet_google_calendar_events';

function mapConnection(data: {
  connected: boolean;
  email?: string;
  connectedAt?: string;
  syncEnabled?: boolean;
}): GoogleCalendarConnection {
  return {
    connected: Boolean(data.connected),
    email: data.email,
    connectedAt: data.connectedAt ? new Date(data.connectedAt).getTime() : undefined,
    syncEnabled: Boolean(data.syncEnabled ?? data.connected),
  };
}

function parseEvents(raw: Array<{
  id: string;
  title: string;
  start: string;
  end: string;
  location?: string;
  googleEventId?: string;
}>): CalendarEvent[] {
  return raw.map((e) => ({
    id: e.id.startsWith('google_') ? e.id : `google_${e.id}`,
    source: 'google' as const,
    title: e.title,
    start: new Date(e.start),
    end: new Date(e.end),
    allDay: false,
    googleEventId: e.googleEventId ?? e.id.replace(/^google_/, ''),
    location: e.location,
    color: 'blue' as const,
  }));
}

export async function fetchGoogleCalendarStatus(): Promise<GoogleCalendarConnection> {
  try {
    const response = await api.get('/api/calendar/google/status');
    return mapConnection(response.data.data ?? response.data);
  } catch {
    return { connected: false, syncEnabled: false };
  }
}

export async function getGoogleCalendarAuthUrl(): Promise<string> {
  const response = await api.get('/api/calendar/google/auth-url');
  const data = response.data.data;
  if (!data?.url || !data?.state) {
    throw new Error('Sunucudan geçerli OAuth URL alınamadı');
  }

  sessionStorage.setItem('tbkmeet_gcal_oauth_state', data.state);
  return data.url;
}

function calendarApiError(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const res = (error as { response?: { status?: number; data?: { error?: string } } }).response;
    if (res?.data?.error) return res.data.error;
    if (res?.status === 401) return 'Oturum süresi dolmuş. Tekrar giriş yapın.';
    if (res?.status === 404) return 'Takvim API bulunamadı. Nginx /api/calendar/ proxy ekleyin.';
    if (res?.status === 503) return res.data?.error || 'Google OAuth sunucuda yapılandırılmamış.';
  }
  return 'Bağlantı başlatılamadı. Ağ veya sunucu hatası.';
}

export { calendarApiError };

export async function completeGoogleCalendarCallback(
  code: string,
  state: string | null
): Promise<GoogleCalendarConnection> {
  const response = await api.post('/api/calendar/google/callback', {
    code,
    state: state ?? undefined,
  });
  return mapConnection(response.data.data);
}

export async function disconnectGoogleCalendar(): Promise<void> {
  await api.delete('/api/calendar/google/disconnect');
  localStorage.removeItem(EVENTS_CACHE_KEY);
}

export function readGoogleCalendarEventsCache(): CalendarEvent[] {
  try {
    const raw = localStorage.getItem(EVENTS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Array<{
      id: string;
      title: string;
      start: string;
      end: string;
      location?: string;
      googleEventId?: string;
    }>;
    return parseEvents(parsed);
  } catch {
    return [];
  }
}

export async function syncGoogleCalendarEvents(): Promise<CalendarEvent[]> {
  const status = await fetchGoogleCalendarStatus();
  if (!status.connected) return [];

  try {
    const response = await api.get('/api/calendar/google/events');
    const raw = (response.data.data ?? response.data ?? []) as Array<{
      id: string;
      title: string;
      start: string;
      end: string;
      location?: string;
      googleEventId?: string;
    }>;
    localStorage.setItem(EVENTS_CACHE_KEY, JSON.stringify(raw));
    return parseEvents(raw);
  } catch {
    return readGoogleCalendarEventsCache();
  }
}

export type PushMeetingPayload = {
  meetingId: string;
  title: string;
  description?: string;
  scheduledAt: string;
  durationMinutes: number;
  meetingFormat: 'ONLINE' | 'IN_PERSON';
  location?: string;
  address?: string;
  meetingCode?: string;
  timezone?: string;
};

/** Planlı toplantıyı bağlı Google Takvime yazar (bağlı değilse sessizce atlar). */
export async function pushMeetingToGoogleCalendar(
  payload: PushMeetingPayload
): Promise<{ pushed: boolean; googleEventId?: string }> {
  const status = await fetchGoogleCalendarStatus();
  if (!status.connected || !payload.scheduledAt) {
    return { pushed: false };
  }

  const body = {
    ...payload,
    meetingId: String(payload.meetingId),
    title: String(payload.title).trim(),
    scheduledAt: String(payload.scheduledAt),
    durationMinutes: Number(payload.durationMinutes) || 60,
    meetingFormat:
      payload.meetingFormat === 'IN_PERSON' ? ('IN_PERSON' as const) : ('ONLINE' as const),
    description: payload.description?.trim() || undefined,
    location: payload.location?.trim() || undefined,
    address: payload.address?.trim() || undefined,
    meetingCode: payload.meetingCode?.trim() || undefined,
    timezone: payload.timezone?.trim() || undefined,
  };

  try {
    const response = await api.post('/api/calendar/google/events/push-meeting', body);
    const googleEventId = response.data?.data?.googleEventId as string | undefined;
    return { pushed: true, googleEventId };
  } catch (error) {
    const apiError =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { error?: unknown }; status?: number } }).response
        : undefined;
    console.warn(
      'Google Takvime eklenemedi:',
      apiError?.status,
      apiError?.data?.error ?? error
    );
    return { pushed: false };
  }
}
